import { Component, ErrorInfo, ReactNode } from 'react'
import { AppProviders } from './providers'
import { AppRouter } from './routes'
import { OfflineBanner, PWAUpdatePrompt, InstallPrompt } from '@/shared/components'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Error boundary to catch React errors
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('App error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background-dark p-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-display font-bold text-error mb-4">
              Oops! Something went wrong
            </h1>
            <p className="text-text-secondary mb-6">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-accent-primary text-white font-display font-bold rounded-xl hover:bg-accent-primary/90 transition-colors"
            >
              Reload App
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Main App component
 */
export default function App() {
  return (
    <ErrorBoundary>
      <AppProviders>
        <OfflineBanner />
        <AppRouter />
        <PWAUpdatePrompt />
        <InstallPrompt />
      </AppProviders>
    </ErrorBoundary>
  )
}
